import React, { useState } from 'react';
import { Card, CardHeader, CardBody, Collapse, Table } from 'reactstrap';

import { formatDate } from '../tenderOpeningFormatters';

// Shared read-only Cargo table for the Tender Opening Detail tabs.
//
// Three variants:
//   'requirement' (default) — expandable cargo rows revealing the nested
//                             Crude/Condensate sub-table (Bleeding 1 & 2).
//   'hps'                    — same as requirement, but the nested crude table
//                             adds Alpha Dyed / Alpha Undyed columns and rows
//                             start collapsed (rule §Tab Sending Request HPS).
//   'freight'                — a flat (non-expandable) cargo table with the four
//                             freight columns (rule §Tab Request Freight
//                             Availability).
//
// The expand pattern mirrors ProcurementRequirementView: a second <tr> whose
// single <td colSpan> holds a reactstrap <Collapse> with the nested table.
const CrudeSubTable = ({ crudes = [], withAlpha }) => (
  <div className='p-3 bg-light-subtle'>
    <div className='fw-semibold fs-13 mb-2'>Crude / Condensate</div>
    <Table className='table-sm align-middle table-nowrap mb-0 bg-white'>
      <thead className='table-light'>
        <tr>
          <th>Crude / Condensat</th>
          <th>Klasifikasi</th>
          <th>Max Capacity</th>
          <th>Remark Max Capacity</th>
          <th className='text-center'>Is Via CIB Only?</th>
          <th className='text-center'>% Bleeding Crude (1)</th>
          <th className='text-center'>% Bleeding Crude (2)</th>
          {withAlpha && <th className='text-center'>Alpha Dyed</th>}
          {withAlpha && <th className='text-center'>Alpha Undyed</th>}
        </tr>
      </thead>
      <tbody>
        {crudes.length === 0 ? (
          <tr>
            <td
              colSpan={withAlpha ? 9 : 7}
              className='text-center text-muted py-2'
            >
              No crude / condensate data
            </td>
          </tr>
        ) : (
          crudes.map(cr => (
            <tr key={cr.id}>
              <td>{cr.crudeName}</td>
              <td>{cr.klasifikasi ?? '-'}</td>
              <td>{cr.maxCapacity ?? '-'}</td>
              <td>{cr.remarkMaxCapacity || '-'}</td>
              <td className='text-center'>
                <span
                  className={`badge ${
                    cr.isViaCibOnly
                      ? 'bg-success-subtle text-success'
                      : 'bg-secondary-subtle text-secondary'
                  }`}
                >
                  {cr.isViaCibOnly ? 'Yes' : 'No'}
                </span>
              </td>
              <td className='text-center'>{cr.bleeding1 ?? '-'}</td>
              <td className='text-center'>{cr.bleeding2 ?? '-'}</td>
              {withAlpha && <td className='text-center'>{cr.alphaDyed}</td>}
              {withAlpha && <td className='text-center'>{cr.alphaUndyed}</td>}
            </tr>
          ))
        )}
      </tbody>
    </Table>
  </div>
);

const CargoTable = ({ cargos = [], variant = 'requirement' }) => {
  const [openCargo, setOpenCargo] = useState({});
  const withAlpha = variant === 'hps';

  // Freight variant: a flat table with the four freight columns, no expand.
  if (variant === 'freight') {
    return (
      <Card className='border shadow-none mt-1'>
        <CardHeader className='fw-semibold bg-light'>List Cargo</CardHeader>
        <CardBody className='p-0'>
          <div className='table-responsive'>
            <Table className='table-sm align-middle table-nowrap mb-0'>
              <thead className='table-light'>
                <tr>
                  <th>Type</th>
                  <th>OT</th>
                  <th>Volume</th>
                  <th>DDR</th>
                  <th>Tujuan</th>
                  <th className='text-center'>Freight Lumpsum</th>
                  <th className='text-center'>Laytime</th>
                  <th className='text-center'>Demurrage PDPR</th>
                  <th className='text-center'>R2</th>
                </tr>
              </thead>
              <tbody>
                {cargos.length === 0 ? (
                  <tr>
                    <td colSpan={9} className='text-center text-muted py-3'>
                      No cargo data
                    </td>
                  </tr>
                ) : (
                  cargos.map((c, idx) => (
                    <tr key={c.id || idx}>
                      <td>{c.cargoType}</td>
                      <td>{c.ot}</td>
                      <td>{c.volume ?? '-'}</td>
                      <td>
                        {formatDate(c.ddrFrom)} - {formatDate(c.ddrTo)}
                      </td>
                      <td>
                        {c.plant} - {c.unit}
                      </td>
                      <td className='text-center'>{c.freightLumpsum}</td>
                      <td className='text-center'>{c.laytime}</td>
                      <td className='text-center'>{c.demurragePdpr}</td>
                      <td className='text-center'>{c.r2}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    );
  }

  // Requirement / HPS variants: expandable cargo rows -> nested crude table.
  return (
    <Card className='border shadow-none mt-1'>
      <CardHeader className='fw-semibold bg-light'>List Cargo Data</CardHeader>
      <CardBody className='p-0'>
        <div className='table-responsive'>
          <Table className='table-sm align-middle table-nowrap mb-0'>
            <thead className='table-light'>
              <tr>
                <th style={{ width: 40 }}></th>
                <th>Type</th>
                <th>OT</th>
                <th>Volume</th>
                <th>DDR</th>
                <th>DDR Notes</th>
                <th>Incoterms</th>
                <th>Incoterms Notes</th>
                <th>Tujuan</th>
              </tr>
            </thead>
            <tbody>
              {cargos.length === 0 ? (
                <tr>
                  <td colSpan={9} className='text-center text-muted py-3'>
                    No cargo data
                  </td>
                </tr>
              ) : (
                cargos.map((c, idx) => {
                  const key = c.id || idx;
                  const open = !!openCargo[key];
                  return (
                    <React.Fragment key={key}>
                      <tr>
                        <td className='text-center'>
                          <button
                            className='btn btn-sm btn-icon btn-soft-secondary'
                            title={open ? 'Collapse' : 'Expand'}
                            onClick={() =>
                              setOpenCargo(prev => ({ ...prev, [key]: !open }))
                            }
                          >
                            <i
                              className={`ri-arrow-${open ? 'up' : 'down'}-s-line`}
                            ></i>
                          </button>
                        </td>
                        <td>{c.cargoType}</td>
                        <td>{c.ot}</td>
                        <td>{c.volume ?? '-'}</td>
                        <td>
                          {formatDate(c.ddrFrom)} - {formatDate(c.ddrTo)}
                        </td>
                        <td>{c.ddrNotes || '-'}</td>
                        <td>{(c.incoterms || []).join(', ')}</td>
                        <td>{c.incotermsNotes || '-'}</td>
                        <td>
                          {c.plant} - {c.unit}
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={9} className='p-0 border-0'>
                          <Collapse isOpen={open}>
                            <CrudeSubTable
                              crudes={c.crudes}
                              withAlpha={withAlpha}
                            />
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};

export default CargoTable;
